import WRAP from 'WRAP-JS';
import { groupBy, orderBy, debounce } from "lodash-es";
import i18n from "i18next";
import { lazy } from "react";
import { stringToCode, unicodeToUtf8, utf8ToEucJp } from "./encoding/Encoding";

export const deepCopy = (obj) => {
  return JSON.parse(JSON.stringify(obj));
}
export const formatLonLatDM = (lon, lat, decimal=1) => {
  return `${formatLatDM(lat, decimal)} ${formatLonDM(lon, decimal)}`;
};

export const formatLatDM = (lat, decimal=1) => {
  return `${fmtDM(lat, decimal)}′${nsew(lat, true)}`;
};
export const formatLonDM = (lon, decimal=1) => {
  return `${fmtDM(lon, decimal)}′${nsew(lon,false)}`;
};
export const formatLonLatATC1 = (lon, lat) => {
  const _lon = splitDMS(lon);
  const _lat = splitDMS(lat);
  // e.g. "4620N07805W"
  return (
    ("00" + _lat[1]).slice(-2) +
    ("00" + (_lat[2] + Math.round(_lat[3] / 60))).slice(-2) +
    (_lat[0] >= 0 ? "N" : "S") +
    ("000" + _lon[1]).slice(-3) +
    ("00" + (_lon[2] + Math.round(_lon[3] / 60))).slice(-2) +
    (_lon[0] >= 0 ? "E" : "W")
  );
}
export const formatLonLatATC2 = (lon, lat) => {
  const _lon = splitDMS(lon);
  const _lat = splitDMS(lat);
  // e.g. "46N078W"
  return (
    ("00" + (_lat[1] + Math.round(_lat[2] / 60))).slice(-2) +
    (_lat[0] >= 0 ? "N" : "S") +
    ("000" + (_lon[1] + Math.round(_lon[2] / 60))).slice(-3) +
    (_lon[0] >= 0 ? "E" : "W")
  );
}

export const zeroPadding = (num, length) => {
  let numStr = String(num);
  const numLength = numStr.length;

  if (numLength < length) {
    const zeroCount = length - numLength;
    for (let i = 0; i < zeroCount; i++) {
      numStr = `0${numStr}`;
    }
  }
  return numStr;
};

export const getPerspective = (plan) => {
  if (!plan.route) return;
  let points = convertRouteToWRAPPoints(plan.route);
  points.push(convertWRAPPoint(plan.dep.lat, plan.dep.lon));
  points.push(convertWRAPPoint(plan.arr.lat, plan.arr.lon));
  const perspective =  WRAP.Geo.getPerspective(points, 85);

  return {
    centerLat: m2d(perspective.center.lat),
    centerLon: m2d(perspective.center.lon),
    zoom: perspective.fractional_zoom
  }
};

export const getRoutePerspective = (coordinates) => {
  if (!Array.isArray(coordinates) || coordinates.length === 0) return;
  const _coordinates = Array.isArray(coordinates[0]) ? coordinates : [coordinates];
  const points = _coordinates.map(coordinate => {
    return convertWRAPPoint(coordinate[1], coordinate[0]);
  });
  const perspective = WRAP.Geo.getPerspective(points, 85);

  return {
    centerLat: m2d(perspective.center.lat),
    centerLon: m2d(perspective.center.lon),
    zoom: perspective.fractional_zoom
  }
};

export const isReverseRoute = (depLon, arrLon) => {
  let d_lon = arrLon - depLon;
  if ( d_lon < 0 ) {
    d_lon += 360.0;
  } else if ( d_lon >= 360.0 ) {
    d_lon -= 360.0;
  }
  return d_lon > 180.0;
}

/**
 * プランのルート配列をWRAP.Geo.Pointの配列に変換する.
 * @param {Array} route プランのルート配列
 * @returns {Array} WRAP.Geo.Pointの配列に変換する
 */
export const convertRouteToWRAPPoints = (route) => {
  const _route = route || [];
  return _route.flatMap((point) => {
    if (point.airway) {
      return point.airway.reduce((list, ap) => {
        list.push(convertWRAPPoint(ap.lat, ap.lon));
        return list;
      },[]);
    } else {
      return convertWRAPPoint(point.lat, point.lon)
    }
  });
};

/**
 * 指定された緯度経度をWRAP.Geo.Pointに変換する
 * @param {number} lat 緯度
 * @param {number} lon 経度
 * @return {WRAP.Geo.Point}
 */
export const convertWRAPPoint = (lat, lon) =>{
  return new WRAP.Geo.Point(d2m(lat), d2m(lon));
};

/**
 * 星型のシンボルを描画するための座標配列を返却する.
 * @param {number} size サイズ
 * @returns {Array[]} 星型の座標配列
 */
export const makeStarSymbol = function(size) {

  const base = 238;
  const symbol = [[0,-113],[29,-27],[119,-27],[47,27],[73,113],[0,62],[-73,113],[-47,27],[-119,-27],[-29,-27],[0,-113]];

  return symbol.map(v => {
    return [
      v[0] * size / base,
      v[1] * size / base
    ];
  });

};

/**
 * 三角型のシンボルを描画するための座標配列を返却する.
 * @param {number} size サイズ
 * @returns {Array[]} 三角型の座標配列
 */
export const makeTriangleSymbol = function(size) {

  var base = 10;
  var symbol = [[0,-5], [5,5], [-5,5], [0,-5]];

  return symbol.map(v => {
    return [
      v[0] * size / base,
      v[1] * size / base
    ];
  });

};

/**
 * 角丸長方形を描画するための座標配列を返却する.
 * @param {Array[[x, y], ...]} rect 矩形の座標配列
 * 2座標、4座標、5座標(パスクローズ)の形式
 * @param {number} radius 角の半径
 * @returns {Array[[x, y], ...]} 角丸長方形の座標配列
 */
export const makeRoundedRectangle = (rect, radius) => {
  const _rect = [];
  switch(rect.length) {
    case 2:
      _rect.push(
        [rect[0][0], rect[0][1]],
        [rect[0][0], rect[1][1]],
        [rect[1][0], rect[1][1]],
        [rect[1][0], rect[0][1]],
      );
      break;
    case 4:
      _rect.push(...rect);
      break;
    case 5:
      _rect.push(...rect.slice(0, 4));
      break;
    default:
      return rect;
  }

  const sizes = [];
  for(let i = 1; i <= 2; i ++) {
    const p1 = _rect[i-1];
    const p2 = _rect[i];
    const w = p2[0] - p1[0];
    const h = p2[1] - p1[1];
    sizes.push(Math.sqrt( w * w + h * h ) / 2);
  }

  const _radius = Math.min(radius, ...sizes);

  const get = (n) => {
    if (n < 0) {
      return _rect[_rect.length - 1];
    } else if (n >= _rect.length) {
      return _rect[0];
    }
    return _rect[n];
  }

  const ret = [];
  _rect.forEach((_, i) => {
    const c = get(i);
    const p = get(i - 1);
    const q = get(i + 1);
    const s = (c[0] - p[0]) * (c[1] - q[1]) - (c[1] - p[1]) * (c[0] - q[0]);
    const o = [
      c[0] + Math.sign(p[0] - c[0] + q[0] - c[0]) * _radius,
      c[1] + Math.sign(p[1] - c[1] + q[1] - c[1]) * _radius,
    ];
    const theta = Math.atan2(c[1] - o[1], c[0] - o[0]) + Math.sign(s) * Math.PI / 4;
    const partition = Math.ceil(Math.sqrt(_radius)) + 1;
    const dt = Math.PI / 2 / partition * Math.sign(-s);
    Array(partition + 1).fill(0).forEach((_, j) => {
      const rad = theta + dt * j;
      ret.push([o[0] + _radius * Math.cos(rad), o[1] + _radius * Math.sin(rad)]);
    });
  });
  ret.push(ret[0]);
  return ret;
}

/**
 * 度を分に変換する.
 * @param {number|string} degree 度
 * @returns {number} 分
 */
export const d2m = (degree) => {
  return parseFloat(degree) * 60.0;
};

/**
 * 分を度に変換する.
 * @param {number|string} minutes 分
 * @returns {number} 度
 */
export const m2d = (minutes) => {
  return parseFloat(minutes) / 60.0;
};

/**
 * 度をラジアンに変換する.
 * @param {number} deg 度
 * @returns {number} ラジアン
 */
export const deg2rad = (deg) => {
  return deg * Math.PI / 180;
};

/**
 * 度をラジアンに変換する.
 * @param {number} rad ラジアン
 * @returns {number} 度
 */
export const rad2deg = (rad) => {
  return rad * 180 / Math.PI;
};

/**
 * 角度を0<=degree<360の範囲に正規化し返却する.
 * @param degree 角度
 * @returns {number} 正規化された角度
 */
export const normalizeDegree = (degree) => {
  while (degree < 0) {
    degree += 360;
  }
  return degree % 360;
};

/**
 * 2つのベクトルのなす角の中間の角度を算出する。
 *
 * @param {number[]|LonLat} o 基点となる座標
 * @param {number[]|LonLat} p1 ベクトル1
 * @param {number[]|LonLat} p2 ベクトル2
 * @returns {number} 算出した角度
 */
export const calcMiddleAngle = (o, p1, p2) => {

  let xy, xy1, xy2;
  if (Array.isArray(o)) {
    xy = WRAP.Geo.getScreenPoint(convertWRAPPoint(o[1], o[0]));
  } else {
    xy = WRAP.Geo.getScreenPoint(convertWRAPPoint(o.lat, o.lon));
  }

  if (Array.isArray(p1)) {
    xy1 = WRAP.Geo.getScreenPoint(convertWRAPPoint(p1[1], p1[0]));
  } else {
    xy1 = WRAP.Geo.getScreenPoint(convertWRAPPoint(p1.lat, p1.lon));
  }

  if (Array.isArray(p2)) {
    xy2 = WRAP.Geo.getScreenPoint(convertWRAPPoint(p2[1], p2[0]));
  } else {
    xy2 = WRAP.Geo.getScreenPoint(convertWRAPPoint(p2.lat, p2.lon));
  }

  var a = [xy2.x - xy.x, xy2.y - xy.y];
  var b = [xy1.x - xy.x, xy1.y - xy.y];
  // var angle = (Math.atan2(a[1], a[0]) + Math.atan2(b[1], b[0])) / 2;
  // return rad2deg(angle);

  let angle = 0;
  let count = 0;
  [a, b].forEach(a => {
    if(a[0] !== 0 || a[1] !== 0) {
      angle += Math.atan2(a[1], a[0]);
      count++;
    }
  });
  if(count === 0) {
    return angle;
  // } else if(count === 1) {
  //   return normalizeDegree(rad2deg(angle) + 90);
  } else {
    return normalizeDegree(rad2deg(angle / count));
  }

};

/**
 * フィートをメートルに変換する
 * @parame {number} フィート
 * @return {number} メートル
 */
export const ft2m = ft => {
  return ft * 0.3048;
};

/**
 * メートルをフィートに変換する
 * @parame {number} メートル
 * @return {number} フィート
 */
export const m2ft = m => {
  return m / 0.3048;
};

/**
 * 海里をメートルに変換する
 * @parame {number} メートル
 * @return {number} 海里
 */
 export const nm2m = nm => {
  return nm * 1852.0;
}

/**
 * メートルを海里に変換する
 * @parame {number} メートル
 * @return {number} 海里
 */
 export const m2nm = m => {
  return m / 1852.0;
}

const _1kt = 1852.0 / 3600;
/**
 * 秒速メートルをノットに変換する
 * @parame {number} 秒速メートル
 * @return {number} ノット
 */
export const mps2kt = mps => {
  return mps / _1kt;
}

/**
 * ノットを秒速メートルに変換する
 * @parame {number} ノット
 * @return {number} 秒速メートル
 */
export const kt2mps = kt => {
  return kt * _1kt;
}

/**
 * 国際標準大気モデルにおいて、高度から気圧を算出する。
 * @param {number} h 高度(m)
 * @returns {number} 気圧(hPa/mb)
 * @exception 0m未満および20,000m超の高度を指定された場合
 */
export const calcPressureFromAlt =(h) => {

  if( h < 0 || h > 20000 ) {

    throw new Error('out of range');

  }

  if( h < 11000 ) {

    // 0 - 11,000 m
    //
    // P0 * ( 1 - ( 0.0065 * h ) / T0 ) ** 5.257
    //
    // P0: 1013.25 hPa
    // T0: 15 °C = 288.15 K
    return 1013.25 * Math.pow( 1 - ( 0.0065 * h ) / 288.15, 5.257 );
  } else {

    // 11,000 - 20,000 m
    //
    // P0 * exp( g / 286.95 * ( 11000 - h ) / T0 );
    //
    // P0: 226.32 hPa
    // g: 9.81 m/s/s
    // T0: -56.5 °C = 216.65 K
    return 226.32 * Math.exp( 9.81 / 286.95 * ( 11000 - h ) / 216.65 );
  }

};

/**
 * 指定されたズームレベルにおいて、画面上の 1 ピクセルが地表上で何メートルに相当するかを算出する.
 * 地図は Web Mercator（EPSG:3857）相当のスケールを前提とする。
 * @param zoom
 * @param lat
 * @returns {number}
 */
export const metersPerPixel = (zoom, lat = 0) => {
  // 地球の赤道周長（メートル）/ 256ピクセル（ズームレベル0のタイル1枚の幅）
  // = 40075016.686 / 256 ≒ 156543.03392
  const METERS_PER_PIXEL_AT_ZOOM_0 = 156543.03392;

  // 緯度の範囲チェック（Web Mercatorの有効範囲は約±85.05度）
  const clampedLat = Math.max(-85.05, Math.min(85.05, lat));

  return (
    METERS_PER_PIXEL_AT_ZOOM_0 *
    Math.cos(clampedLat * Math.PI / 180) /
    Math.pow(2, zoom)
  );
}

/**
 * 指定した緯度において、地表上の 1 メートルが経度方向で何度に相当するかを算出する.
 * @param lat
 * @returns {number}
 */
export const degreesPerMeter = (lat = 0) => {
  // 赤道上での1度あたりのメートル数
  // 地球の赤道周長 40075016.686m / 360度 ≒ 111319.491 ≒ 111320
  const METERS_PER_DEGREE_AT_EQUATOR = 111320;

  // 緯度の範囲チェック（極域を避ける）
  const clampedLat = Math.max(-85, Math.min(85, lat));

  return 1 / (METERS_PER_DEGREE_AT_EQUATOR * Math.cos(clampedLat * Math.PI / 180));
}


function nsew(n, isLat) {
  if (isLat) {
    if (n === 0) return '';
  } else {
    if (n % 180 === 0) return ''
  }
  const dir = ['N', 'S', 'E', 'W'];
  const idx = isLat ? 0 : 2;
  return n > 0 ? dir[idx] : dir[idx + 1];
}

function fmtDM(n, decimal = 1) {
  const depth = Math.pow(10, decimal);
  const abs = Math.abs(n);
  const d = Math.floor(abs);
  const m = (abs - d) * 60;
  const mi = Math.floor(m * depth) / depth;
  return `${d}°${mi}`
}

export const splitDMS = (degree) => {
  let d = Math.abs(degree);
  const sign = Math.sign(degree);
  const deg = Math.floor(d);
  d -= deg;
  const min = Math.floor(d * 60);
  d -= min / 60;
  const sec = d * 3600;
  return [sign, deg, min, sec];
}

/**
 * 文字列が示す日時(UTC)のDateインスタンスを返却します.
 * @param {string} str 日時文字列
 * @returns {Date} Dateインスタンス 日時として解釈できなかった場合はnull
 */
export const parseUTCDate = str => {

  let dateList;
  if((dateList = /^(\d{4})[/\- ]?(\d{1,2})[/\- ]?(\d{1,2})[T ]?(\d{1,2})[: ]?(\d{1,2})[: ]?(\d{1,2})Z?$/.exec(str)) !== null) {
    dateList = dateList.slice(1);
    dateList[1]--;
  } else if((dateList = /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)[/\- ](\d{1,2})[/\- ](\d{4}) (\d{1,2})[: ]?(\d{1,2})[: ]?(\d{1,2})$/i.exec(str)) !== null) {
    dateList = dateList.slice(1);
    const month = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"].indexOf(dateList[0].toLowerCase());
    dateList[0] = dateList[2];
    dateList[2] = dateList[1];
    dateList[1] = month;
  }

  if(dateList) {
    return new Date(Date.UTC.apply(Date, dateList));
  }
  return null;
}

/**
 * 日時のDateインスタンスを指定の書式で整形し返却します.
 * 日時はUTCに基づきます.
 * @param {Date} date Dateインスタンス
 * @param {string} [fmt] 日付書式
 * @returns {string} 整形された日時
 */
export const formatUTCDate = (date, fmt) => {
  if(!date) {
    return;
  }
  if(!fmt) {
    fmt = '%Y/%m/%d %H:%M:%S';
  }

  return fmt.replace(/%[_-]?[a-zA-Z%]/g, (match) => {

    let idx = 1;
    let padding;
    switch(match.charAt(1)) {
      case '-':
        idx++;
        padding = '';
        break;
      case '_':
        idx++;
        padding = ' ';
        break;
      default:
        padding = '0';
        break;
    }

    switch(match.charAt(idx)) {
      //Date
      case 'Y':
        return date.getUTCFullYear();
      case 'm':
        return (padding + (date.getUTCMonth() + 1)).slice(-2);
      case 'd':
        return (padding + date.getUTCDate()).slice(-2);
      case 'a':
        return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"][date.getUTCDay()];
      case 'A':
        return ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"][date.getUTCDay()];
      case 'b':
        return ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][date.getUTCMonth()];
      case 'B':
        return ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"][date.getUTCMonth()];
      case 'w':
        return date.getUTCDay();
      //Time
      case 'H':
        return (padding + date.getUTCHours()).slice(-2);
      case 'k':
        return date.getUTCHours();
      case 'I':
        return (padding + (date.getUTCHours() % 12 === 0 ? 12 : date.getUTCHours() % 12)).slice(-2);
      case 'l':
        return date.getUTCHours() % 12 === 0 ? 12 : date.getUTCHours() % 12;
      case 'M':
        return (padding + date.getUTCMinutes()).slice(-2);
      case 'S':
        return (padding + date.getUTCSeconds()).slice(-2);
      case 'p':
        return date.getUTCHours() < 12 ? 'AM' : 'PM';
      case '%':
        return '%';
      default:
        return;
    }

  });

}

export const formatTimeZoneDate = (dateTime, timeZone = WRAP.Core.timezone, format= "YYYY/MM/DD HH:mmz") => {
  if (!dateTime) return null;
  if (dateTime === '---') return dateTime;
  if (timeZone === null) timeZone = 'utc';
  const dt = new WRAP.Core.DateTime(dateTime);
  return dt.format(format, timeZone);
};

/**
 * 時制パネル用Basetimeフォーマット.
 * @param time
 * @return {string}
 */
export const formatBaseTime = time => {
  if (time === '---') return time;
  return `(Base: ${formatTimeZoneDate(time, WRAP.Core.timezone, 'MM/DD HH:mmz')})`;
}
/**
 * 時制パネル用Validtimeフォーマット.
 * @param time
 * @return {string}
 */
export const formatValidTime = time => {
  if (time === '---') return time;
  return `Valid: ${formatTimeZoneDate(time, WRAP.Core.timezone, 'YYYY/MM/DD HH:mmz')}`;
}
/**
 * 時制パネル用Issuetimeフォーマット.
 * @param time
 * @return {string}
 */
 export const formatIssueTime = time => {
   if (time === '---') return time;
  return `(Issue: ${formatTimeZoneDate(time, WRAP.Core.timezone, 'MM/DD HH:mmz')})`;
}

/**
 * FlightPlan用時刻フォーマット
 * @param time
 * @param colon
 * @return {string}
 */
export const formatHHMM = (time, colon=true) => {
  if (!time || time === '---') return time;
  const format = colon ? 'HH:mm' : 'HHmm';
  return formatTimeZoneDate(time, WRAP.Core.timezone, format);
}

/**
 * タイムゾーン文字列(UTC/JST)フォーマット
 * @param timeZone
 * @return {string}
 */
 export const formatTimeZoneStr = (timeZone = WRAP.Core.timezone) => {
  const dt = new WRAP.Core.DateTime();
  const tz = timeZone === 'utc' ? null : timeZone;
  return dt.format('z', tz);
};

/**
 * 秒を時分秒の形式に変換
 * @param sec
 */
export const sec2hms = (sec) => {
  const _sec = sec % 60;
  const _min = Math.floor(sec / 60) % 60;
  const _hour = Math.floor(sec / 3600);
  return {
    hour: _hour,
    min: _min,
    sec: _sec
  }
};

/**
 * ランダムなIDを生成する.
 * @return {string}
 */
export const uuid = () => {
  const d = +new Date();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
    .replace(/[xy]/g, function(c) {
      const r = (d + Math.random() * 16) % 16 | 0;
      // eslint-disable-next-line
      return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
    });
};

/**
 * 現在の言語設定を取得する。※ReactのコンポーネントではuseTranslationを使用すること
 * @return {string}
 */
export const language = () => {
  return i18n.language;
};
export const isJa = () => {
  return language() === 'ja';
};
export const isUtc = () => {
  return WRAP.Core.timezone === 'utc' || WRAP.Core.timezone === null;
};
/**
 * 翻訳する。※ReactのコンポーネントではuseTranslationを使用すること
 * @param word
 * @param {Object} replaceObject
 * @return {string}
 */
export const translation = (word, replaceObject) => {
  return i18n.t(word, replaceObject);
};

export const isVisibleElement = (element) => {
  return element.offsetParent !== null;
}

let _buffer;
export const textareaLineCount = (textarea) => {
  if (!_buffer) {
    _buffer = document.createElement('textarea');
    _buffer.style.border = 'none';
    _buffer.style.height = '0';
    _buffer.style.overflow = 'hidden';
    _buffer.style.padding = '0';
    _buffer.style.position = 'absolute';
    _buffer.style.left = '0';
    _buffer.style.top = '0';
    _buffer.style.zIndex = '-1';
    document.body.appendChild(_buffer);
  }

  let cs = window.getComputedStyle(textarea);
  let pl = parseInt(cs.paddingLeft);
  let pr = parseInt(cs.paddingRight);
  let lh = parseInt(cs.lineHeight);

  // [cs.lineHeight] may return 'normal', which means line height = font size.
  if (isNaN(lh)) lh = parseInt(cs.fontSize);

  // Copy content width.
  _buffer.style.width = (textarea.clientWidth - pl - pr) + 'px';

  // Copy text properties.
  _buffer.style.font = cs.font;
  _buffer.style.letterSpacing = cs.letterSpacing;
  _buffer.style.whiteSpace = cs.whiteSpace;
  _buffer.style.wordBreak = cs.wordBreak;
  _buffer.style.wordSpacing = cs.wordSpacing;
  _buffer.style.wordWrap = cs.wordWrap;

  // Copy value.
  _buffer.value = textarea.value;

  let result = Math.floor(_buffer.scrollHeight / lh);
  if (result === 0) result = 1;
  return result;
};

/**
 * Shift_JISで半角１バイト全角２バイトで計算
 * @param str
 * @return {number}
 */
export const strBytes = str => {
  let a = 0;
  let d = escape(str);
  let c = d.length;
  if (d) {
    for (let b = 0; b < c; b++) {
      if (d.charAt(b) === "%" && d.charAt(b + 1) === "u") {
        b += 5;
        a++
      } else {
        if (d.charAt(b) === "%" && (d.charAt(b + 1) === "2" || d.charAt(b + 1) === "3" || d.charAt(b + 1) === "5" || d.charAt(b + 1) === "7")) {
          b += 2
        }
      }
      a++
    }
    return a
  } else {
    return 0
  }
};

/**
 * Shift_JISで半角１バイト全角２バイトで計算
 * @param str
 * @return {number}
 */
export const strSJISBytes = str => {
  if (typeof str === 'string') {
    return str.length * 2 - Array.from(str.matchAll(/[ -~｡-ﾟ]/g)).length;
  }
  return 0;
}

/**
 * 引数の文字列がEUC-JPでサポートされているか判定する
 * @param str
 */
export const checkEucJp = str => {
  const codes = stringToCode(str);
  const utf8Code = unicodeToUtf8(codes);
  const eucCode = utf8ToEucJp(utf8Code)
  return eucCode !== null;
}

export const escapeHtml = str => {
  if (typeof str !== 'string') {
    return str;
  }
  return str.replace(/[&'`"<>]/g, (match) => {
    switch(match) {
      case '&':
        return '&amp;';
      case '\'':
        return '&#039;';
      case '`':
        return '&#096;';
      case '"':
        return '&quot;';
      case '<':
        return '&lt;';
      case '>':
        return '&gt;';
      default:
        return match;
    }
  });
}

/**
 * 空チェック.
 * isEmpty(null)
 *  →true
 * isEmpty(undefined)
 *  →true
 * isEmpty("")
 *  →true
 * isEmpty(1)
 *  →true
 * isEmpty(0)
 *  →true
 * isEmpty([])
 *  →true
 * isEmpty({})
 *  →true
 * isEmpty("a")
 *  →false
 * isEmpty([1])
 *  →false
 * isEmpty([{a:1}])
 *  →false
 * @param value
 * @return {boolean}
 */
export const isEmpty = (value) => {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value.length === 'number') {
    return !value.length;
  }
  if (typeof value.size === 'number') {
    return !value.size;
  }
  const type = Object.prototype.toString.call(value).slice(8, -1)
  if (type === 'Object') {
    return !Object.keys(value).length;
  }
  return true;
};

/**
 * Object比較.
 * isEqualObject({a:1,b:2},{a:1,b:2})
 *  →true
 * isEqualObject({a:1,b:2},{b:2,a:1})
 *  →true
 * isEqualObject({a:1,b:2,nest:{c:3}},{nest:{c:3},b:2,a:1})
 *  →true
 * isEqualObject({a:['a']},{a:['a']})
 *  →true
 * isEqualObject(['a',{a:1,b:2}],['a',{b:2,a:1}])
 *  →true
 * isEqualObject({a:1,b:2},{a:1,b:9})
 *  →false
 * @param a
 * @param b
 * @return {boolean}
 */
export const isEqualObject = (a, b) => {
  if (a === b) return true;

  if (typeof a !== 'object' || typeof b !=='object' || a === null || b === null) {
    return false;
  }

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);

  if (aKeys.length !== bKeys.length) {
    return false;
  }

  for (let key of aKeys) {
    if (!bKeys.includes(key)) {
      return false;
    }
    if (!isEqualObject(a[key], b[key])) {
      return false;
    }
  }
  return true;
}

export const getProperty = (obj, deepKey) => {
  const path = deepKey.split('.');
  let index = 0;
  const length = path.length;
  while (obj != null && index < length) {
    obj = obj[path[index]];
    index++;
  }
  return (index && index === length) ? obj : undefined;
}

export const setProperty = (obj, deepKey, value, opt) => {
  let o = obj;
  const keys = deepKey.split('.');
  const last = keys.length - 1;
  keys.forEach((key, i) => {
    if (i === last) {
      if (value instanceof Object && opt && opt.assign) {
        o[key] = Object.assign({}, o[key], value);
      } else {
        o[key] = value;
      }
      return;
    }
    if (!(o[key] instanceof Object)) {
      o[key] = {};
    }
    o = o[key];
  });
  return obj;
}

export const removeProperty = (obj, deepKey) => {
  const keys = deepKey.split('.');
  let currentObj = obj;
  for (let i = 0; i < keys.length - 1; i++) {
    currentObj = currentObj[keys[i]];
    if (!currentObj) break;
  }
  if (currentObj) {
    delete currentObj[keys[keys.length - 1]];
    // Check if the parent object is empty and remove it if it is
    if (Object.keys(currentObj).length === 0 && currentObj.constructor === Object) {
      const parentKey = keys.slice(0, -1).join('.');
      if (parentKey.includes('.')) {
        removeProperty(obj, parentKey)
      } else {
        delete obj[parentKey];
      }
    }
  }
}

export const extractBy = (list, property, comparator) => {
  let result = undefined;
  if (!list || list.length === 0 || !property || !comparator) return result;

  let iteratee;
  if (typeof property === 'function') {
    iteratee = property;
  } else {
    iteratee = getProperty;
  }
  let index = -1;
  let computed = undefined ;
  while (++index < list.length) {
    let value = list[index];
    let current = iteratee(value, property);
    if (current != null) {
      if (computed === undefined) {
        computed = current;
        result = value;
      } else {
        if (comparator(current, computed)) {
          computed = current;
          result = value;
        }
      }
    }
  }
  return result;
}

export const maxBy = (list, property) =>{
  return extractBy(list, property, (a, b) => {
    return a > b
  });
}

export const minBy = (list, property) =>{
  return extractBy(list, property, (a, b) => {
    return a < b
  });
}

/**
 * コンポーネントの遅延読み込み.
 * @param factory
 * @param timeout
 * @return {React.LazyExoticComponent<T>}
 */
export const lazyIdle = (factory, timeout = 5000) => {
  return lazy(() =>
    new Promise((resolve) => {
      window.requestIdleCallback(() => {
        resolve(factory());
      }, {timeout});
    })
  );
};

export const to360degreesRange = (point) => {
  let lon = Array.isArray(point) ? point[0] : point;

  if (lon < 0) {
    lon = (lon + 360) % 360;
  }
  if (Array.isArray(point)) {
    point[0] = lon;
    return point;
  } else {
    return lon;
  }
};

//lodash解禁関数
export const _groupBy = groupBy;
export const _orderBy = orderBy;

export const _debounce = debounce
